<template>
  <app-modal
    ref="modal"
    v-bind="$attrs"
    class="modal-container"
    content-class="modal-medium"
    title="アプライアンス契約変更"
  >
    <template v-slot:body="{ cancel }">
      <cross-validation-observer v-slot="{ handleSubmit }" as="div">
        <v-form novalidate @submit.stop.prevent="handleSubmit(submit)">
          <v-card-text class="pa-0">
            <div class="modal-body">
              <v-container class="px-1">
                <app-badge
                  :item="
                    isUtmOrIfw === 'UTM' ? 'UTM契約' : 'インターネットFW契約'
                  "
                  :isRequired="false"
                  :isInfo="false"
                />
                <v-checkbox
                  hide-details
                  v-model="applianceAccepted"
                  label="解約する"
                  density="compact"
                ></v-checkbox>
                <span class="ml-2 mt-1">
                  ※Type1のUTMおよびインターネットFWでは、解約後は再契約できません。<br />
                </span>
                <span class="ml-2">
                  ※Type1のUTMおよびインターネットFWを解約するには以下を事前に実施してください。<br />
                </span>
                <span class="ml-3"> ・お客様追加ルールの削除<br /> </span>
                <span class="ml-3">
                  （編集不可デフォルトポリシーおよび編集可能デフォルトポリシーの削除は不要）<br />
                </span>
                <span class="ml-3"> ・インターネット一時停止 </span>

                <app-badge
                  item="NAT契約"
                  :isRequired="false"
                  :isInfo="false"
                  class="mt-4"
                />
                <v-radio-group
                  inline
                  density="comfortable"
                  v-model="contractChangeType1Appliance.natEnabled"
                  hide-details
                >
                  <v-radio :value="false" label="解約" />
                  <v-radio
                    :value="true"
                    label="契約"
                    :disabled="applianceAccepted"
                  />
                </v-radio-group>
                <template v-if="contractGetType1Appliance.natEnabled">
                  <span class="ml-2">
                    ※Type1のNATを解約するには以下を事前に実施してください。<br />
                  </span>
                  <span class="ml-3"> ・お客様追加ルールの削除<br /> </span>
                  <span class="ml-3">
                    ・編集可能デフォルトルールの削除（編集不可デフォルトポリシーの削除は不要）
                  </span>
                </template>

                <app-label-group
                  label="NATルール数"
                  :value="`${contractGetType1Appliance.natRuleCount} 個`"
                  class="mt-4"
                ></app-label-group>
              </v-container>
            </div>
            <!-- 関連項目チェック: 初期表示した内容から設定内容に変更があること。-->
            <cross-validation-provider
              :passed="objectChanged"
              vid="no_change"
              class="mb-4"
            >
              {{ $filter.message("no_change_data") }}
            </cross-validation-provider>
          </v-card-text>
          <v-divider></v-divider>
          <v-card-actions>
            <v-list-item class="w-100 pa-1">
              <template v-slot:append>
                <v-btn type="submit" variant="flat" color="primary">
                  変更
                </v-btn>
                <v-btn variant="outlined" color="primary" @click="cancel">
                  戻る
                </v-btn>
              </template>
            </v-list-item>
          </v-card-actions>
        </v-form>
      </cross-validation-observer>
    </template>
  </app-modal>
</template>

<script lang="ts">
import type { PropType } from "vue";
import { defineComponent } from "vue";
import InternetType1ContractApplianceConfirm from "@/modals/applianceContractSetting/type1/appliance/InternetType1ContractApplianceConfirm.vue";
import type {
  ContractGetType1Appliance,
  ContractChangeType1Appliance,
} from "@/apis/ContractChangeType1Api";
import { VueFinalModal } from "vue-final-modal";

export default defineComponent({
  name: "InternetType1ContractApplianceModify",
  props: {
    contractGetType1Appliance: {
      type: Object as PropType<ContractGetType1Appliance>,
      required: true,
    },
  },
  data() {
    return {
      alertMsg: null as string | null,
      contractChangeType1Appliance: {
        ...this.contractGetType1Appliance,
        note: null,
      } as ContractChangeType1Appliance,
      /**
       * UTMorIFWどちらの契約を実施しているか判定を格納
       * UTM:UTM契約
       * IFW:IFW契約
       */
      isUtmOrIfw: "",
      /**
       * アプライアンスを解約したかを格納
       * true:解約
       * false:契約
       */
      applianceAccepted: false,
      natChange: false,
    };
  },
  computed: {
    /** 関連項目チェック
     * 初期表示した内容から設定内容に変更があること。
     */
    objectChanged(): boolean {
      // アプライアンスを解約した場合、無条件で変更あり
      if (this.applianceAccepted) {
        return true;
        // NAT契約を変更した場合、変更あり
      } else if (
        this.contractGetType1Appliance.natEnabled !==
        this.contractChangeType1Appliance.natEnabled
      ) {
        return true;
      } else {
        return false;
      }
    },
  },
  watch: {
    /**
     *  アプライアンス解約にチェック
     *    NAT契約を解約にする
     *    アプライアンス(UTM/IFW)を解約にする
     *  アプライアンス解約のチェックを外す
     *    NAT契約/アプライアンス(UTM/IFW)を変更前の値に戻す
     * */
    applianceAccepted: function (val: boolean) {
      if (val) {
        this.contractChangeType1Appliance.natEnabled = false;
        this.contractChangeType1Appliance.utmEnabled = false;
        this.contractChangeType1Appliance.ifwEnabled = false;
      } else {
        this.contractChangeType1Appliance.natEnabled =
          this.contractGetType1Appliance.natEnabled;
        this.contractChangeType1Appliance.utmEnabled =
          this.contractGetType1Appliance.utmEnabled;
        this.contractChangeType1Appliance.ifwEnabled =
          this.contractGetType1Appliance.ifwEnabled;
      }
    },
  },
  async mounted() {
    /** UTM契約、IFW契約どちらの契約が対象か判断する */
    if (this.contractGetType1Appliance.utmEnabled) {
      this.isUtmOrIfw = "UTM";
    } else {
      this.isUtmOrIfw = "IFW";
    }
  },
  methods: {
    /** 契約変更確認画面を表示 */
    async submit() {
      this.contractChangeType1Appliance.contractType1KeyId =
        this.contractGetType1Appliance.contractType1KeyId;
      // 確認モーダルを表示
      await this.$modal.show(InternetType1ContractApplianceConfirm, {
        contractChangeType1Appliance: this.contractChangeType1Appliance,
        isUtmOrIfw: this.isUtmOrIfw,
        applianceChange: this.applianceAccepted ? true : false,
        natChange:
          this.contractGetType1Appliance.natEnabled !==
          this.contractChangeType1Appliance.natEnabled,
      });
      // 情報更新
      await this.$api.contract_change_type1.postContractType1Appliance(
        this.contractChangeType1Appliance
      );
      if (
        !this.contractChangeType1Appliance.utmEnabled &&
        !this.contractChangeType1Appliance.ifwEnabled
      ) {
        // アプライアンス契約(UTM/IFW)が解約された場合、プロビジョニング完了後に再取得してvuexのデータを更新
        await this.$store.dispatch(
          "provisioning/addCompletedEvent",
          async () => {
            await this.$store.dispatch("user/updateLoginInfo", {
              contractSummary: await this.$api.contract.getSummary(),
            });
          }
        );
      } else {
        // NATのみ変更された場合、プロビジョニング走らないため、そのまま再取得してvuexのデータを更新
        await this.$store.dispatch("user/updateLoginInfo", {
          contractSummary: await this.$api.contract.getSummary(),
        });
      }
      (this.$refs.modal as typeof VueFinalModal & { ok: () => void }).ok();
    },
  },
});
</script>
<style lang="scss" scoped>
span {
  letter-spacing: normal !important;
}
</style>
